/**
 * Atlas PM · 项目星图 · 边（星轨）
 *
 * 对照原型：
 *   - 核心连线：项目核心 → 每个任务（细线，深蓝 #3D8BFF，opacity 0.24）
 *   - 依赖边：任务间依赖（二次贝塞尔，青蓝 #5EE7FF，opacity 0.2）
 *   - 父子边：父任务 → 子任务（同依赖边样式，稍暗）
 */

import * as THREE from 'three';
// 显式 .ts 后缀：使本模块可被 Node 直接 import（自测脚本无需打包器）
import type { StarLayoutNode } from './layout.ts';

export type StarEdgeKind = 'dep' | 'parent';

/** 边输入（由真实依赖图 / 任务树得到） */
export interface StarEdgeSpec {
  from: string;
  to: string;
  kind: StarEdgeKind;
}

export interface BuiltEdge {
  line: THREE.Line;
  a: string;
  b: string;
  kind: string;
}

export interface EdgeBuild {
  group: THREE.Group;
  edges: BuiltEdge[];
}

/** `#RRGGBB` → 0xRRGGBB（非法回退灰蓝，与 layout.ts 口径一致） */
function hexToNumber(hex: string): number {
  const v = Number.parseInt(hex.replace(/^#/, ''), 16);
  return Number.isFinite(v) ? v : 0x8fb2d0;
}

/** 二次贝塞尔：两端点 + 抬高控制点 */
function bezierPoints(a: THREE.Vector3, b: THREE.Vector3, lift: number): THREE.Vector3[] {
  const mid = a.clone().add(b).multiplyScalar(0.5);
  mid.y += lift;
  const curve = new THREE.QuadraticBezierCurve3(a, mid, b);
  return curve.getPoints(14);
}

export function buildEdges(
  nodes: readonly StarLayoutNode[],
  specs: readonly StarEdgeSpec[],
): EdgeBuild {
  const group = new THREE.Group();
  const edges: BuiltEdge[] = [];
  const byId = new Map<string, StarLayoutNode>();
  for (const n of nodes) byId.set(n.id, n);

  const center = byId.get('__center__');

  // ── 核心连线（中心 → 每个任务）──
  if (center) {
    for (const node of nodes) {
      if (node.isCenter) continue;
      const line = new THREE.Line(
        new THREE.BufferGeometry().setFromPoints([center.pos, node.pos]),
        new THREE.LineBasicMaterial({
          color: 0x3d8bff,
          transparent: true,
          opacity: 0.24,
          depthWrite: false,
          blending: THREE.AdditiveBlending,
        }),
      );
      line.userData = { a: center.id, b: node.id, kind: 'core' };
      group.add(line);
      edges.push({ line, a: center.id, b: node.id, kind: 'core' });
    }
  }

  // ── 依赖 / 父子边（贝塞尔弧）──
  for (const spec of specs) {
    const a = byId.get(spec.from);
    const b = byId.get(spec.to);
    if (!a || !b || a.isCenter || b.isCenter || a === b) continue;
    const isDep = spec.kind === 'dep';
    const geometry = new THREE.BufferGeometry().setFromPoints(bezierPoints(a.pos, b.pos, 1.4));
    // 依赖边按「源节点所属星座」分色（设计稿：同簇同色的星轨）；父子边用虚线区分
    const material = isDep
      ? new THREE.LineBasicMaterial({
          color: hexToNumber(a.clusterColorHex),
          transparent: true,
          opacity: 0.34,
          depthWrite: false,
          blending: THREE.AdditiveBlending,
        })
      : new THREE.LineDashedMaterial({
          color: 0x5a8fc0,
          transparent: true,
          opacity: 0.24,
          dashSize: 0.9,
          gapSize: 0.7,
          depthWrite: false,
          blending: THREE.AdditiveBlending,
        });
    const line = new THREE.Line(geometry, material);
    if (!isDep) line.computeLineDistances();
    line.userData = { a: a.id, b: b.id, kind: spec.kind };
    group.add(line);
    edges.push({ line, a: a.id, b: b.id, kind: spec.kind });
  }

  return { group, edges };
}
